import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, FolderOpen, X } from 'lucide-react';
import {
  createUnit,
  getUnits,
  updateUnit,
  deleteUnit,
  getClasses
} from '../firebase/lessonPlannerService';
import DashboardLayout from './DashboardLayout';
import './Units.css';

function Units() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [selectedClassFilter, setSelectedClassFilter] = useState('all');
  const [formData, setFormData] = useState({
    unitNumber: '',
    title: '',
    description: '',
    duration: '',
    classIds: [],
    standards: ['']
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentUser, navigate]);

  const loadData = async () => {
    setLoading(true);
    const [unitsResult, classesResult] = await Promise.all([
      getUnits(currentUser.id),
      getClasses(currentUser.id)
    ]);
    if (unitsResult.success) {
      setUnits(unitsResult.units);
    }
    if (classesResult.success) {
      setClasses(classesResult.classes);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.classIds.length === 0) {
      alert('Please select at least one class for this unit.');
      return;
    }

    // Filter out empty standards
    const standards = formData.standards.filter(s => s.trim() !== '');

    const unitData = {
      ...formData,
      standards
    };

    if (editingUnit) {
      const result = await updateUnit(editingUnit.id, unitData);
      if (result.success) {
        loadData();
        closeModal();
      }
    } else {
      const result = await createUnit(currentUser.id, unitData);
      if (result.success) {
        loadData();
        closeModal();
      }
    }
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      unitNumber: unit.unitNumber || '',
      title: unit.title,
      description: unit.description || '',
      duration: unit.duration || '',
      classIds: unit.classIds || [],
      standards: unit.standards && unit.standards.length > 0 ? unit.standards : ['']
    });
    setShowModal(true);
  };

  const handleDelete = async (unitId) => {
    if (window.confirm('Are you sure you want to delete this unit?')) {
      const result = await deleteUnit(unitId);
      if (result.success) {
        loadData();
      }
    }
  };

  const handleClassToggle = (classId) => {
    setFormData(prev => ({
      ...prev,
      classIds: prev.classIds.includes(classId)
        ? prev.classIds.filter(id => id !== classId)
        : [...prev.classIds, classId]
    }));
  };

  const getClassNames = (classIds) => {
    if (!classIds || classIds.length === 0) return 'No classes assigned';
    return classIds
      .map(id => classes.find(c => c.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  const handleArrayFieldChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const addArrayField = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const removeArrayField = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUnit(null);
    setFormData({
      unitNumber: '',
      title: '',
      description: '',
      duration: '',
      classIds: [],
      standards: ['']
    });
  };

  const filteredUnits = selectedClassFilter === 'all'
    ? units
    : units.filter(unit => unit.classIds && unit.classIds.includes(selectedClassFilter));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading units...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="units-container">
        <div className="page-header">
          <div>
            <h1>Units</h1>
            <p className="subtitle">Organize your curriculum into units</p>
          </div>
          <button onClick={() => setShowModal(true)} className="btn-primary">
            <Plus size={20} />
            New Unit
          </button>
        </div>

        {/* Class Filter */}
        {classes.length > 0 && units.length > 0 && (
          <div className="filter-section">
            <label>Filter by Class:</label>
            <div className="class-filter-buttons">
              {classes.map(cls => (
                <button
                  key={cls.id}
                  onClick={() => setSelectedClassFilter(selectedClassFilter === cls.id ? 'all' : cls.id)}
                  className={`class-filter-btn ${selectedClassFilter === cls.id ? 'active' : ''}`}
                >
                  {cls.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {filteredUnits.length === 0 && units.length > 0 ? (
          <div className="empty-state">
            <FolderOpen size={64} className="empty-icon" />
            <h3>No units found for selected class</h3>
            <p>Try selecting a different class or create a new unit</p>
          </div>
        ) : units.length === 0 ? (
          <div className="empty-state">
            <FolderOpen size={64} className="empty-icon" />
            <h3>No units yet</h3>
            <p>Create your first unit to get started</p>
            <button onClick={() => setShowModal(true)} className="btn-primary">
              <Plus size={20} />
              Create Unit
            </button>
          </div>
        ) : (
          <div className="units-grid">
            {filteredUnits.map(unit => (
              <div key={unit.id} className="unit-card">
                <div className="card-header">
                  <h3>
                    {unit.unitNumber && <span className="unit-number">Unit {unit.unitNumber}: </span>}
                    {unit.title}
                  </h3>
                  <div className="card-actions">
                    <button onClick={() => handleEdit(unit)} className="btn-icon">
                      <Edit size={18} />
                    </button>
                    <button onClick={() => handleDelete(unit.id)} className="btn-icon delete">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                {unit.description && <p className="card-description">{unit.description}</p>}
                <div className="card-meta">
                  {unit.duration && <span className="tag">{unit.duration}</span>}
                </div>
                <div className="card-classes">
                  <strong>Classes:</strong> {getClassNames(unit.classIds)}
                </div>
              </div>
            ))}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{editingUnit ? 'Edit Unit' : 'Create New Unit'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group" style={{ flex: '0 0 150px' }}>
                    <label>Unit Number</label>
                    <input
                      type="number"
                      value={formData.unitNumber}
                      onChange={(e) => setFormData({ ...formData, unitNumber: e.target.value })}
                      placeholder="e.g., 1"
                      min="0"
                      step="1"
                    />
                  </div>

                  <div className="form-group" style={{ flex: '1' }}>
                    <label>Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      placeholder="e.g., Introduction to Algebra"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Duration (e.g., "4 weeks")</label>
                  <input
                    type="text"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Classes * (Select at least one)</label>
                  {classes.length === 0 ? (
                    <p className="empty-message">No classes available. Please create classes first.</p>
                  ) : (
                    <div className="checkbox-group">
                      {classes.map(cls => (
                        <label key={cls.id} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.classIds.includes(cls.id)}
                            onChange={() => handleClassToggle(cls.id)}
                          />
                          <span>{cls.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* TEKS Standards */}
                <div className="form-group array-field">
                  <label>TEKS Standards</label>
                  {formData.standards.map((standard, index) => (
                    <div key={index} className="array-input-row">
                      <input
                        type="text"
                        value={standard}
                        onChange={(e) => handleArrayFieldChange('standards', index, e.target.value)}
                        placeholder="e.g., 111.39.c.1.A"
                      />
                      {formData.standards.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeArrayField('standards', index)}
                          className="btn-icon-small delete"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayField('standards')}
                    className="add-field-btn"
                  >
                    + Add TEKS Standard
                  </button>
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={closeModal} className="btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingUnit ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Units;
